<template>
	<td width="70%">
		<p v-if="!authorized" v-text="formatValue" />
		<template v-else-if="showForm">
			<v-form ref="form" v-model="valid" @submit.prevent>
				<v-row no-gutters>
					<v-text-field
						v-if="formType == 'text'"
						v-model="newValue"
						:type="type"
						:suffix="suffix"
						:prefix="prefix"
						:rules="rules"
						:hint="hint"
						dense
						outlined
					/>
					<v-row v-if="formType == 'date-select'" no-gutters>
						<v-col class="pr-1" sm="4">
							<v-combobox
								v-model="year"
								dense
								outlined
								:items="years"
								:rules="rules"
								@change="changeDate"
							>
								<template v-slot:append-outer>年</template>
							</v-combobox>
						</v-col>
						<v-col class="px-1" sm="4">
							<v-combobox
								v-model="month"
								dense
								outlined
								:items="months"
								:rules="rules"
								@change="changeDate"
							>
								<template v-slot:append-outer>月</template>
							</v-combobox>
						</v-col>
						<v-col class="pl-1" sm="4">
							<v-combobox
								v-model="day"
								dense
								outlined
								:items="days"
								:rules="rules"
								@change="changeDate"
							>
								<template v-slot:append-outer>日</template>
							</v-combobox>
						</v-col>
					</v-row>

					<v-menu
						v-if="formType == 'date-picker'"
						close-on-content-click
						transition="scale-transition"
						offset-y
						min-width="auto"
					>
						<template v-slot:activator="{ on, attrs }">
							<v-text-field
								v-model="newValue"
								class="mt-4"
								readonly
								outlined
								dense
								v-bind="attrs"
								v-on="on"
								:hint="hint"
								:suffix="suffix"
								:prefix="prefix"
								:rules="rules"
							/>
						</template>
						<v-date-picker no-title locale="ja-JP" />
					</v-menu>
					<v-textarea
						v-if="formType == 'textarea'"
						v-model="newValue"
						:suffix="suffix"
						:prefix="prefix"
						:rules="rules"
						:hint="hint"
						dense
						outlined
						rows="2"
					/>
					<v-select
						v-if="formType == 'select'"
						:items="items"
						item-text="name"
						item-value="id"
						v-model="newValue"
						:suffix="suffix"
						:prefix="prefix"
						:rules="rules"
						:hint="hint"
						dense
						outlined
					/>
					<v-btn
						:class="formType == 'date-select' ? 'mx-auto' : 'ml-2'"
						color="secondary"
						@click="submitForm"
						:disabled="!valid"
						>保存</v-btn
					>
				</v-row>
			</v-form>
		</template>
		<template v-else>
			<v-hover v-slot="{ hover }" close-delay="200" open-delay="200">
				<v-row no-gutters justify="space-between">
					<p v-text="formatValue" />
					<v-btn v-show="hover" small absolute right icon @click="showForm = true"
						><v-icon>mdi-pencil</v-icon></v-btn
					>
				</v-row>
			</v-hover>
		</template>
	</td>
</template>

<script>
import axios from "axios"
import authenticate from "../../helper/authenticate"
import errorHandling from "../../helper/errorHandling"
import common from "../../helper/common"

export default {
	props: {
		value: {
			type: null,
			required: false,
			default: null,
		},
		name: {
			type: null,
			required: false,
			default: null,
		},
		type: String,
		suffix: null,
		prefix: null,
		rules: Array,
		hint: String,
		items: {
			type: Array,
			required: false,
			default: () => [],
		},
		modelName: {
			type: String,
			required: true,
		},
		columnName: {
			type: String,
			required: true,
		},
		url: {
			type: String,
			required: true,
		},
		authorized: {
			type: Boolean,
			required: false,
			default: true,
		},
		dateTime: {
			type: Boolean,
			required: false,
			default: false,
		},
		date: {
			type: Boolean,
			required: false,
			default: false,
		},
		time: {
			type: Boolean,
			required: false,
			default: false,
		},
		formType: {
			type: String,
			required: false,
			default: "text",
		},
	},
	data() {
		return {
			newValue: null,
			showForm: false,
			valid: false,
			year: null,
			month: null,
			day: null,

			common,
		}
	},
	mounted() {
		this.newValue = this.value
		if (this.formType == "date-select" && this.value) {
			this.year = this.value.split("-")[0]
			this.month = this.value.split("-")[1]
			this.day = this.value.split("-")[2]
		}
	},
	methods: {
		changeDate() {
			if (!this.year && !this.month && !this.day) {
				this.newValue = null
				this.valid = true
				return
			}
			if (!this.year || !this.month || !this.day) {
				this.newValue = null
				this.valid = false
				return
			}
			this.newValue = `${this.year}-${this.month}-${this.day}`
			this.valid = true
		},
		submitForm() {
			if (!this.$refs.form.validate()) return
			if (this.value == this.newValue) return (this.showForm = false)
			const headers = authenticate.setHeader()
			let params = {}
			params[this.modelName] = {}
			params[this.modelName][this.columnName] = this.newValue
			axios
				.patch(this.url, params, { headers })
				.then((res) => {
					this.showForm = false
					this.$emit("update-column", res.data)
				})
				.catch((err) => {
					const error = errorHandling.errorResponse(err)
					if (Array.isArray(error)) {
						this.errors = error
					}
				})
		},
	},
	computed: {
		formatValue() {
			let value = this.name ? this.name : this.value
			if (value === null || value === "") return " "

			if (this.dateTime) {
				value = common.formatDateTime(value)
			} else if (this.date) {
				value = common.formatDate(value)
			} else if (this.time) {
				value = common.formatTime(value)
			}

			if (this.suffix && this.prefix) {
				return `${this.prefix} ${value} ${this.suffix}`
			} else if (this.suffix) {
				return `${value} ${this.suffix}`
			} else if (this.prefix) {
				return `${this.prefix} ${value}`
			} else {
				return value
			}
		},
		years() {
			const thisYear = new Date().getFullYear()
			const endYear = 1900
			const years = [...Array(thisYear - endYear + 1)].map((_, i) => thisYear - i)
			return years
		},
		months() {
			const months = [...Array(12)].map((_, i) => i + 1)
			return months
		},
		days() {
			const days = [...Array(31)].map((_, i) => i + 1)
			return days
		},
	},
	watch: {
		value(newVal) {
			this.newValue = newVal
		},
		deep: true,
	},
}
</script>